import { useState } from "react";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from "@/components/ui/dialog";
import { Sparkles, AlertTriangle, Check } from "lucide-react";
import { toast } from "sonner";

/**
 * AI draft assistant — advisory only. Presents Gemini-generated line
 * suggestions for the user to approve before adding to the current
 * estimator lines. Never sends estimates. Never commits without approval.
 */
export default function AiDraftDialog({ open, onClose, customerId, propertyId, onApprove }) {
  const [prompt, setPrompt] = useState("");
  const [loading, setLoading] = useState(false);
  const [draft, setDraft] = useState(null);
  const [selected, setSelected] = useState({}); // idx -> bool

  const run = async () => {
    if (!prompt.trim()) return;
    setLoading(true);
    setDraft(null);
    try {
      const { data } = await api.post("/estimator/ai/draft", {
        prompt,
        customer_id: customerId || null,
        property_id: propertyId || null,
        include_measurements: true,
        max_lines: 8,
      });
      setDraft(data);
      // Pre-select all suggested lines
      const initSel = {};
      (data.lines || []).forEach((_, i) => { initSel[i] = true; });
      setSelected(initSel);
    } catch (e) {
      const detail = e?.response?.data?.detail || e?.message || "AI draft failed.";
      toast.error(detail);
    } finally { setLoading(false); }
  };

  const approve = () => {
    const chosen = (draft.lines || []).filter((_, i) => selected[i]);
    if (chosen.length === 0) { toast.error("Select at least one line to add"); return; }
    onApprove(chosen);
    reset();
    onClose();
  };

  const reset = () => { setPrompt(""); setDraft(null); setSelected({}); };

  return (
    <Dialog open={open} onOpenChange={(v) => { if (!v) { reset(); onClose(); } }}>
      <DialogContent className="sm:max-w-2xl max-h-[90vh] overflow-y-auto" data-testid="ai-draft-dialog">
        <DialogHeader>
          <DialogTitle className="font-heading text-2xl flex items-center gap-2">
            <Sparkles className="w-5 h-5 text-[#FF5A00]"/> AI Draft Assistant
          </DialogTitle>
        </DialogHeader>

        <div className="text-xs text-zinc-500 mb-2">
          Describe the job in plain English. The AI drafts line items using your service catalog and this property's measurements. <b>Nothing is added until you approve.</b>
        </div>

        <Textarea
          value={prompt}
          onChange={(e) => setPrompt(e.target.value)}
          placeholder="e.g. Pressure wash the house (about 2,400 sqft), clean gutters, mulch two beds (front 300 sqft, side 200 sqft, 3in deep)."
          rows={4}
          disabled={loading}
          data-testid="ai-draft-prompt"
        />

        <div className="flex items-center gap-2 mt-3">
          <Button
            onClick={run}
            disabled={loading || !prompt.trim()}
            className="bg-[#FF5A00] hover:bg-[#E04F00] text-white"
            data-testid="ai-draft-run"
          >
            <Sparkles className="w-4 h-4 mr-1"/> {loading ? "Drafting…" : "Draft with AI"}
          </Button>
          <span className="text-[10px] uppercase tracking-widest text-zinc-500 font-mono-accent">
            Advisory only · Human review required
          </span>
        </div>

        {draft && (
          <div className="mt-4 space-y-3" data-testid="ai-draft-results">
            {draft.warnings && draft.warnings.length > 0 && (
              <div className="border border-amber-300 bg-amber-50 rounded-lg p-3 text-xs text-amber-900" data-testid="ai-draft-warnings">
                <div className="flex items-center gap-1 font-bold mb-1"><AlertTriangle className="w-3.5 h-3.5"/> AI warnings</div>
                <ul className="list-disc pl-5 space-y-0.5">
                  {draft.warnings.map((w, i) => <li key={i}>{w}</li>)}
                </ul>
              </div>
            )}
            <div className="text-xs uppercase tracking-widest text-zinc-500 font-mono-accent">Suggested lines</div>
            {(draft.lines || []).length === 0 && (
              <div className="text-sm text-zinc-500">The AI did not propose any lines. Try adding more detail.</div>
            )}
            <div className="space-y-2">
              {(draft.lines || []).map((l, i) => (
                <label
                  key={i}
                  className={`flex items-start gap-3 border rounded-lg p-3 cursor-pointer transition-colors ${
                    selected[i] ? "border-[#FF5A00] bg-orange-50" : "border-zinc-200"
                  }`}
                  data-testid={`ai-draft-line-${i}`}
                >
                  <input
                    type="checkbox"
                    className="mt-1"
                    checked={!!selected[i]}
                    onChange={(e) => setSelected({ ...selected, [i]: e.target.checked })}
                    data-testid={`ai-draft-line-check-${i}`}
                  />
                  <div className="min-w-0 flex-1">
                    <div className="text-sm font-medium">{l.service_name || l.calculator_key || "Line"}</div>
                    <div className="text-xs text-zinc-600 font-mono-accent">
                      {Number(l.quantity || 0).toLocaleString()} {l.unit || ""}
                      {l.calculator_key && <span className="ml-2 text-zinc-400">· {l.calculator_key}</span>}
                      {l.service_id && <span className="ml-2 text-emerald-700">· matched service</span>}
                      {l.measurement_id && <span className="ml-2 text-indigo-700">· uses saved measurement</span>}
                    </div>
                    {l.assumption && <div className="text-xs text-zinc-500 mt-1 italic">"{l.assumption}"</div>}
                  </div>
                </label>
              ))}
            </div>
          </div>
        )}

        <DialogFooter className="mt-4">
          <Button type="button" variant="outline" onClick={() => { reset(); onClose(); }}>Cancel</Button>
          {draft && (
            <Button
              type="button"
              onClick={approve}
              className="bg-emerald-600 hover:bg-emerald-700 text-white"
              data-testid="ai-draft-approve"
            >
              <Check className="w-4 h-4 mr-1"/> Add selected lines
            </Button>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
